/**
 * VideoVariantsTabContent — Variants tab content for the Video Generator bottom panel.
 *
 * Contains: video history grid with selection, pending spinners, empty state.
 * Video cells are 16:9 aspect ratio in a 2-column grid layout.
 */

import { memo, useState, useRef, forwardRef, useImperativeHandle, useCallback } from 'react'
import { Play, Square } from 'lucide-react'
import { cn } from '../../../../lib/utils'
import type { VideoHistoryEntry } from './VideoHistoryPopover'
import { VideoPreviewPlayer, type VideoPreviewPlayerHandle } from './VideoPreviewPlayer'

/**
 * Compute dynamic height for the Variants tab content area.
 * 1-2 variants -> 1 row; 3-4 -> 2 rows; 5+ -> 7 rows (with scroll).
 * Video cells are always 16:9, 2-column grid.
 */
export function getVideoVariantsHeight(variantCount: number, nodeWidth: number = 400): number {
  if (variantCount === 0) return 150

  // Panel is calc(100% + 120px) wide; min 632px
  const panelWidth = Math.max(nodeWidth + 120, 632)
  // p-3 = 24px total horizontal padding; gap-2 = 8px; 2 columns
  const cellWidth = (panelWidth - 24 - 8) / 2
  const cellHeight = cellWidth / (16 / 9)
  const gap = 8
  const padding = 24
  const playAllHeaderHeight = variantCount >= 2 ? 40 : 0

  const rows = Math.ceil(variantCount / 2)

  if (rows <= 2) {
    return Math.round(rows * cellHeight + (rows - 1) * gap + padding + playAllHeaderHeight)
  }

  // 5+ variants: cap at 7 rows with scroll
  return Math.round(7 * cellHeight + 6 * gap + padding + playAllHeaderHeight)
}

const VideoHistoryCell = memo(forwardRef<VideoPreviewPlayerHandle, {
  entry: VideoHistoryEntry
  realIdx: number
  isSelected: boolean
  buildVideoUrl: (localPath: string) => string | null
  onSelect: (index: number) => void
  onEnded?: () => void
}>(function VideoHistoryCell(
  { entry, realIdx, isSelected, buildVideoUrl, onSelect, onEnded },
  ref,
) {
  const [isHovering, setIsHovering] = useState(false)
  const playerRef = useRef<VideoPreviewPlayerHandle>(null)
  const videoPath = entry.result?.local_path
  const videoUrl = videoPath ? buildVideoUrl(videoPath) : null

  // Forward the internal playerRef to the parent via the forwarded ref
  useImperativeHandle(ref, () => playerRef.current!, [])

  return (
    <div
      className={cn(
        'relative rounded-lg overflow-hidden border-2 transition-all cursor-pointer group',
        isSelected
          ? 'border-primary ring-2 ring-primary/30 p-0.5'
          : 'border-transparent hover:border-border'
      )}
      onClick={() => onSelect(realIdx)}
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
    >
      <div
        className="rounded-md overflow-hidden"
        style={{ aspectRatio: '16/9' }}
      >
        {videoUrl ? (
          <VideoPreviewPlayer
            ref={playerRef}
            videoUrl={videoUrl}
            isHovering={isHovering}
            showExpand={true}
            showDownload={true}
            onEnded={onEnded}
            onExpand={() => {
              // Future: open fullscreen/lightbox for this variant
            }}
            onDownload={() => {
              const a = document.createElement('a')
              a.href = videoUrl
              a.download = `variant-${realIdx + 1}.mp4`
              a.click()
            }}
          />
        ) : (
          <div className="w-full h-full bg-surface-2 flex items-center justify-center">
            <svg
              className="w-4 h-4 text-text-muted/30"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1}
                d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
              />
            </svg>
          </div>
        )}
      </div>
      <div className="absolute bottom-1.5 left-1.5 text-xxs text-white bg-black/60 px-1 rounded z-20">
        v{realIdx + 1}
      </div>
    </div>
  )
}))

VideoHistoryCell.displayName = 'VideoHistoryCell'

export interface VideoVariantsTabContentProps {
  history: VideoHistoryEntry[]
  pickedIndex: number
  onSelectVariant: (index: number) => void
  batchPending: number
  buildVideoUrl: (localPath: string) => string | null
}

export const VideoVariantsTabContent = memo(function VideoVariantsTabContent({
  history,
  pickedIndex,
  onSelectVariant,
  batchPending,
  buildVideoUrl,
}: VideoVariantsTabContentProps) {
  const variantCount = history.length + batchPending

  // Play All state
  const playerRefs = useRef<(VideoPreviewPlayerHandle | null)[]>([])
  const [isPlayingAll, setIsPlayingAll] = useState(false)
  const finishedCountRef = useRef(0)
  const totalPlayableRef = useRef(0)

  const handlePlayAll = useCallback(() => {
    const refs = playerRefs.current
    let playable = 0
    finishedCountRef.current = 0

    for (const handle of refs) {
      if (!handle?.videoElement) continue
      const video = handle.videoElement
      video.currentTime = 0
      video.muted = true
      video.play().catch(() => {})
      playable++
    }

    if (playable > 0) {
      totalPlayableRef.current = playable
      setIsPlayingAll(true)
    }
  }, [])

  const handleStopAll = useCallback(() => {
    for (const handle of playerRefs.current) {
      if (!handle?.videoElement) continue
      handle.videoElement.pause()
    }
    setIsPlayingAll(false)
    finishedCountRef.current = 0
  }, [])

  const handleVideoEnded = useCallback(() => {
    finishedCountRef.current++
    if (finishedCountRef.current >= totalPlayableRef.current) {
      setIsPlayingAll(false)
      finishedCountRef.current = 0
    }
  }, [])

  // Keep refs array in sync with history length
  playerRefs.current.length = history.length

  return (
    <div className="h-full p-3 overflow-y-auto nowheel">
      {variantCount === 0 ? (
        <div className="flex flex-col items-center justify-center h-full text-center">
          <svg
            className="w-8 h-8 text-text-muted/30 mb-2"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1}
              d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
            />
          </svg>
          <p className="text-xs text-text-muted">
            No videos generated yet
          </p>
          <p className="text-xxs text-text-muted/60 mt-1">
            Generate to create variants
          </p>
        </div>
      ) : (
        <>
          {/* Play All header */}
          {history.length >= 2 && (
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-text-secondary font-medium">
                {history.length} variant{history.length !== 1 ? 's' : ''}
              </span>
              <button
                onClick={isPlayingAll ? handleStopAll : handlePlayAll}
                className={cn(
                  'flex items-center gap-1 px-2 py-1 rounded text-xs transition-colors',
                  isPlayingAll
                    ? 'bg-red-500/20 text-red-400 hover:bg-red-500/30'
                    : 'bg-white/10 text-text-secondary hover:bg-white/20 hover:text-foreground'
                )}
                data-testid="play-all-button"
              >
                {isPlayingAll ? (
                  <>
                    <Square size={12} fill="currentColor" />
                    <span>Stop</span>
                  </>
                ) : (
                  <>
                    <Play size={12} fill="currentColor" />
                    <span>Play All</span>
                  </>
                )}
              </button>
            </div>
          )}
          <div className="grid grid-cols-2 gap-2">
            {/* Pending spinners */}
            {Array.from({ length: batchPending }).map(
              (_, i) => (
                <div
                  key={`pending-${i}`}
                  className="rounded-lg bg-surface-2 flex items-center justify-center border border-border-subtle"
                  style={{ aspectRatio: '16/9' }}
                >
                  <div className="w-4 h-4 border-2 border-yellow-400 border-t-transparent rounded-full animate-spin" />
                </div>
              )
            )}
            {/* History items (newest first) */}
            {[...history]
              .reverse()
              .map((entry, displayIdx) => {
                const realIdx =
                  history.length - 1 - displayIdx
                return (
                  <VideoHistoryCell
                    key={entry.result?.id || realIdx}
                    ref={(handle) => {
                      playerRefs.current[displayIdx] = handle
                    }}
                    entry={entry}
                    realIdx={realIdx}
                    isSelected={realIdx === pickedIndex}
                    buildVideoUrl={buildVideoUrl}
                    onSelect={onSelectVariant}
                    onEnded={handleVideoEnded}
                  />
                )
              })}
          </div>
        </>
      )}
    </div>
  )
})

VideoVariantsTabContent.displayName = 'VideoVariantsTabContent'
